import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const API = process.env.REACT_APP_BACKEND_URL;
const download = async (path, filename) => {
  const res = await fetch(`${API}${path}`, { credentials: "include",
    headers: { "Authorization": `Bearer ${localStorage.getItem("erp_token") || ""}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob(); const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

export default function AllStock() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/stocks").then(r => setItems(r.data.items || [])).catch(e => toast.error(formatErr(e)));
  }, []);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(it =>
      (it.item_name || "").toLowerCase().includes(s) ||
      (it.part_number || "").toLowerCase().includes(s) ||
      (it.hsn_code || "").toLowerCase().includes(s));
  }, [items, q]);
  const pg = usePagination(rows, q);

  const totalValue = useMemo(() => rows.reduce((a, r) => a + (r.stock_value || 0), 0), [rows]);

  return (
    <div className="p-8 print:p-4">
      <div className="flex items-end justify-between mb-6 print:mb-3">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2 print:hidden"><Package className="w-3 h-3"/> Stocks · Stock Data</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">All Stock</h1>
          <div className="text-xs text-muted-foreground mt-1">Complete current stock across all stores and godowns</div>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button data-testid="all-stock-export" variant="outline" onClick={() => download("/api/stocks/export.csv", "all_stock.csv").catch(e => toast.error(formatErr(e)))} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> Export CSV</Button>
          <Button data-testid="all-stock-print" variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-1 md:grid-cols-4 gap-3 print:hidden">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Search</Label>
          <Input data-testid="all-stock-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Item / Part / HSN…" className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="all-stock-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Item Name</th>
              <th className="text-left p-3">Part No</th>
              <th className="text-left p-3">HSN Code</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Group</th>
              <th className="text-left p-3">Unit</th>
              <th className="text-right p-3">Total Qty</th>
              <th className="text-right p-3">Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No items found</td></tr>}
            {pg.pageItems.map(it => (
              <tr key={it.id} data-testid={`all-stock-row-${it.id}`} className="border-t border-border hover:bg-secondary/40">
                <td className="p-3">{it.item_name}</td>
                <td className="p-3 font-mono text-xs text-primary uppercase">{it.part_number}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{it.hsn_code || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{it.category || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{it.group || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{it.unit}</td>
                <td className={`p-3 text-right font-mono font-semibold ${it.low ? "text-destructive" : ""}`}>{it.stock}</td>
                <td className="p-3 text-right font-mono">₹{(it.stock_value || 0).toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border bg-secondary/50 font-semibold">
                <td colSpan={7} className="p-3 text-right uppercase tracking-wider text-xs">Total Value</td>
                <td className="p-3 text-right font-mono" data-testid="all-stock-total">₹{totalValue.toLocaleString("en-IN")}</td>
              </tr>
            </tfoot>
          )}
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
